import { store, store$ } from '@redux/store';
import { distinctUntilChanged, fromEvent, map } from 'rxjs';
import fastdom from 'fastdom';
import { checkIconSvgElement } from '../constants/svgIcons';
import {
  selectAdditionalHomes,
  selectModalStep,
  selectSelectedSlot
} from '../redux/selectors';
import BrokenImageHandler from '../utils/brokenImageHandler';
import formatBooking from '../utils/formatBooking';
import cardSpecSummary from './cardSpecSummary';
import ModalStep from '../enums/modalStep';
import { bookingReset } from '../redux/slices';
import ProgressBar from './progressBar';

export default class TourSummary {
  editButtonClickSubscription = null;

  constructor(rootComponent) {
    this.rootComponent = rootComponent;
    this.progressBar = new ProgressBar();
    this.selectedHomes$ = store$.pipe(
      map(selectAdditionalHomes),
      distinctUntilChanged()
    );
    this.selectedHomes$.subscribe(this.handleSelectedHomesChange.bind(this));
    this.rootComponent.subscribe('domUpdate', this.onDomUpdate.bind(this));
    this.brokenImages = new BrokenImageHandler();
  }

  static getEditButton() {
    return document.querySelector('[data-self-guided-tour-edit-button]');
  }

  static getListContainer() {
    return document.querySelector(
      '[data-self-guided-tour-selected-homes-list]'
    );
  }

  static updateTotalHomesCount(selectedHomes) {
    const totalHomesText = TourSummary.getHomeCountText(selectedHomes.length);
    const totalHomesElement = TourSummary.getTotalHomesElement();
    if (!totalHomesElement) {
      return;
    }
    TourSummary.getTotalHomesElement().innerHTML = totalHomesText;
  }

  static getTotalHomesElement() {
    return document.querySelector('[data-self-guided-summary-total]');
  }

  static getHomeCountText(count) {
    const totalHomes = count + 1;
    const noun = totalHomes > 1 ? 'homes' : 'home';
    return `${totalHomes} ${noun}`;
  }

  onDomUpdate() {
    // we need to re-subscribe to the edit button click event because the button is recreated on every DOM update
    this.brokenImages.unbind();
    if (this.editButtonClickSubscription) {
      this.editButtonClickSubscription.unsubscribe();
      this.editButtonClickSubscription = null;
    }
    // if the edit button is not present (as in the confirmation step), we don't need to do anything
    const editButton = TourSummary.getEditButton();
    if (!editButton) {
      return;
    }
    this.editButtonClickSubscription = fromEvent(editButton, 'click').subscribe(
      () => {
        store.dispatch(bookingReset());
      }
    );
    const listContainer = TourSummary.getListContainer();
    this.brokenImages.bind('[data-on-error-src]', listContainer);
  }

  handleSelectedHomesChange(selectedHomes) {
    TourSummary.updateTotalHomesCount(selectedHomes);
    this.updateCardList(selectedHomes);
  }

  updateCardList(selectedHomes) {
    const cardList = this.renderCardList(selectedHomes);

    // Use fastdom to batch the DOM write
    fastdom.mutate(() => {
      const listContainer = TourSummary.getListContainer();
      if (listContainer) {
        listContainer.innerHTML = cardList;
        this.brokenImages.bind('[data-on-error-src]', listContainer);
      }
    });
  }

  renderCardList(selectedHomes) {
    const specDetails = this.rootComponent.getSpecDetails();
    const { imageUrl, noPhotoUrl, specId, specName } = specDetails;

    const mainHome = `<li class="nhs-u-display--flex nhs-u-width--full">
      ${cardSpecSummary({
        ...specDetails,
        id: specId,
        name: specName,
        imageUrl: imageUrl == null || imageUrl === '' ? noPhotoUrl : imageUrl
      })}
    </li>`;

    const additionalHomes = selectedHomes
      .map(selectedSpecId => this.rootComponent.otherHomes[selectedSpecId])
      .map(
        selected =>
          `<li class="nhs-u-display--flex nhs-u-width--full">${cardSpecSummary({
            id: selected.Id,
            address: selected.Address,
            planName: selected.PlanName,
            squareFeet: selected.SquareFeet,
            bathrooms: selected.Bathrooms,
            bedrooms: selected.Bedrooms,
            garages: selected.Garages,
            price: selected.Price,
            planId: selected.PlanId,
            city: selected.City,
            state: selected.StateName,
            zip: selected.Zip,
            type: selected.CommunityProjectType,
            imageUrl:
              selected.ImageUrl == null || selected.ImageUrl === ''
                ? noPhotoUrl
                : selected.ImageUrl
          })}</li>`
      )
      .join('');

    return mainHome + additionalHomes;
  }

  static renderButton() {
    const backSteps = [
      ModalStep.TourConfiguration,
      ModalStep.TermsAndConditions,
      ModalStep.Questions
    ];

    const state = store.getState();
    const currentStep = selectModalStep(state);
    if (!backSteps.includes(currentStep)) {
      return '';
    }
    return `<button type="button" class="nhs-c-button--secondary nhs-c-button--small-24 nhs-c-button--text-only nhs-u-width--880" data-self-guided-tour-edit-button>Edit</button>`;
  }

  render() {
    const state = store.getState();
    const slot = selectSelectedSlot(state);
    const modalStep = selectModalStep(state);
    const specDetails = this.rootComponent.getSpecDetails();
    const { brandName, communityName } = specDetails;
    const selectedHomes = selectAdditionalHomes(state);
    this.progressBar.setModalStep(modalStep);

    return `<div class="nhs-c-self-guided-right-panel" data-self-guided-summary>
              <hr class="nhs-u-height--25 nhs-u-self-stretch nhs-u-background-color--black-600 nhs-mq-display--none-large" />

              ${this.progressBar.render()}

              <h2 class="nhs-c-heading nhs-o-flex-row-items-start-self-stretch">
                <span class="nhs-c-icon-utour-check nhs-mq-icon-utour-check--56-large">
                  ${checkIconSvgElement}
                </span>
                <span class="nhs-o-typography--souvenir-subhead-2-large-light">You have <span data-self-guided-summary-total>${TourSummary.getHomeCountText(
                  selectedHomes.length
                )}</span> reserved to self-tour at ${communityName} by ${brandName}.</span>
              </h2>
              <div class="nhs-o-flex-column-self-stretch nhs-u-gap--600 nhs-mq-booking-detail--large">
                <div class="nhs-o-flex-column-self-stretch nhs-u-gap--300">
                  <div class="nhs-o-flex-row-items-start-justify-content-space-between">
                    <h3 class="nhs-o-typography--sans-body-1-medium-medium">Your self-tour booking details</h3>
                    ${TourSummary.renderButton()}
                   </div>

                   <p class="nhs-o-typography--sans-subhead-2-large-regular">${formatBooking(
                     slot,
                     true
                   )}</p>

                  <ol class="nhs-o-flex-column-items-start nhs-u-gap--300" data-self-guided-tour-selected-homes-list>
                    ${this.renderCardList(selectedHomes)}
                  </ol>
                </div>
                <hr class="nhs-u-height--25 nhs-u-self-stretch nhs-u-background-color--black-600 nhs-mq-display--none-large" />
            </div>
      </div>`;
  }
}
